/**
 * ConnectionsShareTab Component
 *
 * Tab content for sharing projects with connections.
 * Shows list of connected users with quick share buttons.
 * Integrates into ProjectSharingDialog.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Link2,
  UserPlus,
  Loader2,
  Search,
  Check,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useConnections,
  useConnectionLoading,
  useConnectionActions,
} from '@/stores/connection/connection-store';
import { ProjectsApi } from '@/services/api/projects';
import { FOREST_GREEN } from '@/styles/tokens';
import { toast } from 'sonner';
import type { UserConnection } from '@/types/connections';

interface ConnectionsShareTabProps {
  projectId: string;
  existingCollaboratorIds: string[];
  onCollaboratorAdded?: () => void;
}

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a connection
 */
const getDisplayName = (connection: UserConnection): string => {
  const { user } = connection;
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.username;
};

/**
 * Individual connection row for sharing
 */
interface ConnectionShareRowProps {
  connection: UserConnection;
  isExistingCollaborator: boolean;
  onShare: (userId: string, role: 'viewer' | 'editor') => Promise<void>;
  isSharing: boolean;
}

const ConnectionShareRow: React.FC<ConnectionShareRowProps> = ({
  connection,
  isExistingCollaborator,
  onShare,
  isSharing,
}) => {
  const [selectedRole, setSelectedRole] = useState<'viewer' | 'editor'>('viewer');
  const displayName = getDisplayName(connection);
  const initials = getInitials(
    connection.user.firstName,
    connection.user.lastName,
    connection.user.username
  );

  const handleShare = async () => {
    console.log('[ConnectionShare] Sharing with connection:', connection.user.id, 'role:', selectedRole);
    await onShare(connection.user.id, selectedRole);
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
      <Avatar className="h-10 w-10">
        <AvatarImage src={connection.user.avatar} alt={displayName} />
        <AvatarFallback
          style={{
            backgroundColor: FOREST_GREEN.faint,
            color: FOREST_GREEN.DEFAULT,
          }}
          className="text-sm font-medium"
        >
          {initials}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{displayName}</p>
        <p className="text-xs text-muted-foreground">@{connection.user.username}</p>
      </div>

      {isExistingCollaborator ? (
        <Badge variant="secondary" className="flex items-center gap-1">
          <Check className="h-3 w-3" />
          Already added
        </Badge>
      ) : (
        <div className="flex items-center gap-2">
          <Select
            value={selectedRole}
            onValueChange={(value: 'viewer' | 'editor') => setSelectedRole(value)}
          >
            <SelectTrigger className="w-24 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="viewer">Viewer</SelectItem>
              <SelectItem value="editor">Editor</SelectItem>
            </SelectContent>
          </Select>

          <Button
            size="sm"
            className="h-8"
            onClick={handleShare}
            disabled={isSharing}
            style={{
              backgroundColor: FOREST_GREEN.DEFAULT,
              color: 'white',
            }}
          >
            {isSharing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Loading skeleton
 */
const ConnectionsSkeleton: React.FC = () => (
  <div className="space-y-2">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-4 w-32 mb-1" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-8 w-8" />
      </div>
    ))}
  </div>
);

/**
 * ConnectionsShareTab - Main component
 */
export const ConnectionsShareTab: React.FC<ConnectionsShareTabProps> = ({
  projectId,
  existingCollaboratorIds,
  onCollaboratorAdded,
}) => {
  const connections = useConnections();
  const { isLoading } = useConnectionLoading();
  const { fetchConnections } = useConnectionActions();

  const [searchQuery, setSearchQuery] = useState('');
  const [sharingUserId, setSharingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch connections on mount
  useEffect(() => {
    console.log('[ConnectionShare] Fetching connections...');
    fetchConnections();
  }, [fetchConnections]);

  // Filter connections by search query
  const filteredConnections = connections.filter((conn) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      conn.user.username.toLowerCase().includes(query) ||
      (conn.user.firstName?.toLowerCase() || '').includes(query) ||
      (conn.user.lastName?.toLowerCase() || '').includes(query)
    );
  });

  const handleShare = useCallback(async (userId: string, role: 'viewer' | 'editor') => {
    console.log('[ConnectionShare] Adding collaborator:', userId, 'role:', role);
    setSharingUserId(userId);
    setError(null);

    try {
      const response = await ProjectsApi.addCollaborator(projectId, {
        userId,
        role,
      });

      if (response.success) {
        console.log('[ConnectionShare] Collaborator added successfully');
        toast.success('Connection added as collaborator');
        onCollaboratorAdded?.();
      } else {
        throw new Error(response.error || 'Failed to add collaborator');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add collaborator';
      console.error('[ConnectionShare] Error:', message);
      setError(message);
      toast.error(message);
    } finally {
      setSharingUserId(null);
    }
  }, [projectId, onCollaboratorAdded]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Link2 className="h-5 w-5" style={{ color: FOREST_GREEN.DEFAULT }} />
        <h3 className="text-lg font-semibold">Share with Connections</h3>
      </div>

      <p className="text-sm text-muted-foreground">
        Quickly add your connections as collaborators on this project.
      </p>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search connections..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-destructive text-sm p-2 rounded bg-destructive/10">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Connections list */}
      {isLoading ? (
        <ConnectionsSkeleton />
      ) : filteredConnections.length === 0 ? (
        <div className="text-center py-8">
          <Link2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          {connections.length === 0 ? (
            <>
              <p className="text-muted-foreground">No connections yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Connect with other users to easily share projects
              </p>
            </>
          ) : (
            <p className="text-muted-foreground">No matching connections found</p>
          )}
        </div>
      ) : (
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {filteredConnections.map((connection) => (
            <ConnectionShareRow
              key={connection.connectionId}
              connection={connection}
              isExistingCollaborator={existingCollaboratorIds.includes(connection.user.id)}
              onShare={handleShare}
              isSharing={sharingUserId === connection.user.id}
            />
          ))}
        </div>
      )}

      {/* Stats */}
      {!isLoading && connections.length > 0 && (
        <p className="text-xs text-muted-foreground text-center pt-2 border-t">
          {existingCollaboratorIds.filter((id) =>
            connections.some((c) => c.user.id === id)
          ).length}{' '}
          of {connections.length} connections are already collaborators
        </p>
      )}
    </div>
  );
};

export default ConnectionsShareTab;
